/**
 * useSoldVariants — fetch every variant sold in the last 90 days in one shot,
 * for the COGS settings "Products to add costs for" view.
 *
 * The backend caps results at 500 variants. Most stores have well under that
 * in 90 days; the rare store that exceeds it sees `total_count > variants.length`
 * on the response and the page surfaces a banner explaining the cap. The
 * response also exposes `truncated` — true when the underlying orders fetch
 * hit MAX_PAGES (2,500 orders) — and the page surfaces a separate banner for
 * that case.
 *
 * Pagination is handled entirely client-side (in `Cogs.tsx`) — page changes
 * are pure local state mutations, no network round-trip. `refresh()` re-runs
 * the same fetch (used after a cost save so the "Set" badge updates).
 */

import { useCallback, useEffect, useState } from "react";
import type { SoldVariantsResponse } from "@fbc/shared";
import { apiFetch, ApiError } from "../lib/api.js";

export type UseSoldVariantsState = {
  data: SoldVariantsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
};

const SOLD_VARIANTS_FETCH_LIMIT = 500;

export function useSoldVariants(): UseSoldVariantsState {
  const [data, setData] = useState<SoldVariantsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch<SoldVariantsResponse>(
        `/api/cogs/sold-variants?limit=${SOLD_VARIANTS_FETCH_LIMIT}`,
      );
      setData(r);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not load sold products.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, refresh: load };
}
